import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, InputAdornment, Pagination,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PeopleOutlineOutlinedIcon from "@mui/icons-material/PeopleOutlineOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import api from "../../api/client";
import { PageHeader, SectionCard, StatCard, EmptyState } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";
import { useAuth } from "../../context/AuthContext";

/** Resolve display fields whether API sends flat keys or populated userId. */
function getTimesheetEmployee(t) {
  if (!t) return { name: "", id: null };
  const name = (t.employeeName || t.userId?.name || "").trim() || "Unknown employee";
  const id = (t.employeeid || t.userId?.employeeid || "").trim() || null;
  return { name, id };
}

function hashEmployeeKey(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Stable 1–2 letter initials + distinct background per employee (not only first letter of name/ID). */
function getTimesheetAvatarMeta(t) {
  const { name, id } = getTimesheetEmployee(t);
  const uid =
    t.userId && typeof t.userId === "object" && t.userId._id != null ? String(t.userId._id) : "";
  const stableKey = id || uid || (t._id != null ? String(t._id) : "unknown");

  const isUnknown = !name || name === "Unknown employee";
  let initials = "";
  if (!isUnknown) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      initials = `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
    } else if (parts.length === 1) {
      const w = parts[0];
      if (w.length >= 2) initials = w.slice(0, 2).toUpperCase();
      else if (w.length === 1) initials = w[0].toUpperCase();
    }
  }
  if (!initials && id) {
    const alnum = id.replace(/[^a-zA-Z0-9]/g, "");
    if (alnum.length >= 2) initials = `${alnum[0]}${alnum[alnum.length - 1]}`.toUpperCase();
    else if (alnum.length === 1) initials = `${alnum}${alnum}`.toUpperCase();
    else initials = id.replace(/\s/g, "").slice(0, 2).toUpperCase();
  }
  if (!initials && uid.length >= 2) {
    initials = uid.slice(-2).toUpperCase();
  }
  if (!initials) initials = "?";

  const h = hashEmployeeKey(stableKey);
  const hue = h % 360;
  const sat = 42 + (h % 18);
  const light = 36 + (h % 14);
  const bgcolor = `hsl(${hue} ${sat}% ${light}%)`;

  const rawPic =
    t.userId && typeof t.userId === "object"
      ? String(t.userId.profilePicture || "").trim()
      : "";
  const photoUrl = rawPic || null;

  return {
    initials: initials.slice(0, 2),
    bgcolor,
    photoUrl,
  };
}

function getTimesheetOwnerId(t) {
  if (t == null || t.userId == null) return null;
  if (typeof t.userId === "object" && t.userId._id != null) return String(t.userId._id);
  return String(t.userId);
}

export default function TimesheetAccessNew() {
  const { user, hasRole } = useAuth();
  const viewerId =
    user?._id != null ? String(user._id) : user?.id != null ? String(user.id) : "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const approveTimesheet = async (t) => {
    try {
      await api.post(`/api/timesheet/${t._id}/approve`);
      toast.success("Timesheet approved");
      load();
    } catch (err) { toast.error(extractErrorMessage(err)); }
  };

  const rejectTimesheet = async () => {
    if (!rejectReason.trim()) return toast.error("Rejection reason required");
    try {
      await api.post(`/api/timesheet/${rejectTarget._id}/reject`, { reason: rejectReason });
      toast.success("Timesheet rejected — employee notified");
      setRejectTarget(null);
      setRejectReason("");
      load();
    } catch (err) { toast.error(extractErrorMessage(err)); }
  };

  const statusChip = (status) => {
    const map = { Pending: "warning", Approved: "success", Rejected: "error", Resubmitted: "info", Draft: "default" };
    return <Chip size="small" label={status || "Pending"} color={map[status] || "default"} />;
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/timesheet/logined/getSubordinateTimesheets?page=${page}&limit=15`);
      const d = res.data;
      setRows(Array.isArray(d?.timesheets) ? d.timesheets : d?.data || []);
      const pag = d?.pagination;
      setTotal(d?.total ?? pag?.total ?? d?.allDataTotals?.entries ?? 0);
      const tp = d?.pages ?? pag?.totalPages ?? 1;
      setTotalPages(tp < 1 ? 1 : tp);
    } catch (err) {
      toast.error(extractErrorMessage(err));
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((t) => {
      const { name, id } = getTimesheetEmployee(t);
      return (
        `${name} ${id || ""}`.toLowerCase().includes(q) ||
        (t.taskDetails || []).some((x) => (x.taskName || "").toLowerCase().includes(q))
      );
    });
  }, [rows, search]);

  const stats = useMemo(() => {
    const employees = new Set(
      rows.map((r) => {
        const { id } = getTimesheetEmployee(r);
        return id || (r.userId?._id != null ? String(r.userId._id) : "");
      }).filter(Boolean)
    );
    const totalHours = rows.reduce((s, t) => s + (t.taskDetails || []).reduce((ss, x) => ss + (parseFloat(x.duration) || 0), 0), 0);
    return { employees: employees.size, totalHours: totalHours.toFixed(1), entries: total };
  }, [rows, total]);

  const exportCsv = () => {
    const header = ["Employee ID", "Employee", "Date", "Task", "Project", "Hours"];
    const rowsOut = [];
    filtered.forEach((t) => {
      const { name, id } = getTimesheetEmployee(t);
      (t.taskDetails || []).forEach((task) => {
        rowsOut.push([
          id || "",
          name,
          new Date(t.date).toLocaleDateString("en-IN"),
          task.taskName,
          task.project || task.projectName || "",
          task.duration || "0",
        ]);
      });
    });
    const csv = [header, ...rowsOut].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `team-timesheets-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Box p={3}>
      <PageHeader
        title="Team Timesheets"
        subtitle="Review your reports' work logs"
        icon={<GroupsOutlinedIcon />}
        actions={[
          <Button key="e" variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={exportCsv}>Export CSV</Button>,
        ]}
      />

      {hasRole("manager") && !hasRole("admin", "hr") && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can approve or reject only your team members&apos; timesheets. Your own submissions are not included in this list
          and cannot be approved or rejected by you from this page.
        </Alert>
      )}

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={4}><StatCard label="Entries" value={total} icon={<AccessTimeOutlinedIcon />} accent="#6C50BF" /></Grid>
        <Grid item xs={6} md={4}><StatCard label="Employees" value={stats.employees} icon={<PeopleOutlineOutlinedIcon />} accent="#39C645" /></Grid>
        <Grid item xs={6} md={4}><StatCard label="Hours (page)" value={stats.totalHours} accent="#F9BC06" /></Grid>
      </Grid>

      <SectionCard
        actions={[
          <TextField
            key="s" size="small" placeholder="Search employee or task…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ width: 280 }}
          />,
        ]}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <EmptyState title="No timesheets" subtitle="No team members have submitted timesheets." />
        ) : (
          <Stack spacing={1.5}>
            {filtered.map((t) => {
              const isOpen = !!expanded[t._id];
              const hrs = (t.taskDetails || []).reduce((s, x) => s + (parseFloat(x.duration) || 0), 0);
              const { name: empName, id: empId } = getTimesheetEmployee(t);
              const { initials: avatarInitials, bgcolor: avatarBg, photoUrl: avatarPhoto } = getTimesheetAvatarMeta(t);
              const ownerId = getTimesheetOwnerId(t);
              const isOwnEntry = viewerId !== "" && ownerId != null && ownerId === viewerId;
              return (
                <Box key={t._id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden" }}>
                  <Stack
                    direction="row" alignItems="center" spacing={2}
                    onClick={() => setExpanded((e) => ({ ...e, [t._id]: !e[t._id] }))}
                    sx={{ p: 2, cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
                  >
                    <Avatar
                      src={avatarPhoto || undefined}
                      alt=""
                      sx={{
                        width: 40,
                        height: 40,
                        fontSize: avatarInitials.length > 1 ? 13 : 16,
                        fontWeight: 700,
                        bgcolor: avatarPhoto ? undefined : avatarBg,
                        color: "#fff",
                      }}
                      aria-hidden
                    >
                      {!avatarPhoto && avatarInitials}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography component="div" variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                        {empName}
                      </Typography>
                      <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" sx={{ mt: 0.35 }}>
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                          Employee ID
                        </Typography>
                        <Typography component="span" variant="body2" sx={{ fontFamily: "ui-monospace, Menlo, monospace", fontWeight: 700 }}>
                          {empId || "—"}
                        </Typography>
                      </Stack>
                      <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                        {new Date(t.date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                        {" "} • {t.taskDetails?.length || 0} task(s)
                      </Typography>
                    </Box>
                    <Chip label={`${hrs.toFixed(1)} hrs`} sx={{ bgcolor: hrs >= 8 ? "#EDFAF2" : "#FEFCE8", color: hrs >= 8 ? "#39C645" : "#CA8A04", fontWeight: 700 }} />
                    {statusChip(t.status)}
                    {(t.status === "Pending" || t.status === "Resubmitted") &&
                      (isOwnEntry ? (
                        <Tooltip title="You cannot approve or reject your own timesheet. Open My Timesheets for your entries.">
                          <Chip
                            size="small"
                            variant="outlined"
                            label="Your timesheet"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ fontWeight: 600, maxWidth: { xs: 120, sm: "none" } }}
                          />
                        </Tooltip>
                      ) : (
                        <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" color="success" title="Approve" onClick={() => approveTimesheet(t)}><CheckIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" title="Reject" onClick={() => setRejectTarget(t)}><CloseIcon fontSize="small" /></IconButton>
                        </Stack>
                      ))}
                    <IconButton size="small">{isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                  </Stack>
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box sx={{ borderTop: "1px solid", borderColor: "divider", bgcolor: "background.default" }}>
                      <Box
                        role="status"
                        sx={{
                          px: 2,
                          py: 1.25,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          bgcolor: "action.hover",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, display: "block" }}>
                          Tasks for this employee
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
                          {empName}
                          {empId ? (
                            <Typography component="span" variant="body2" sx={{ fontFamily: "ui-monospace, Menlo, monospace", fontWeight: 700, ml: 1 }}>
                              · {empId}
                            </Typography>
                          ) : null}
                        </Typography>
                      </Box>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Task</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Project</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Hours</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(t.taskDetails || []).map((task) => (
                            <TableRow key={task._id} hover>
                              <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{task.taskName}</TableCell>
                              <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{task.project || task.projectName || "—"}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: "primary.main" }}>{parseFloat(task.duration || 0).toFixed(1)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Stack>
        )}

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
          </Box>
        )}
      </SectionCard>

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reject timesheet</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Rejecting {(() => {
                const { name, id } = getTimesheetEmployee(rejectTarget);
                return id ? `${name} (${id})` : name;
              })()}
              &apos;s timesheet for{" "}
              {rejectTarget?.date && new Date(rejectTarget.date).toLocaleDateString("en-GB")}.
            </Typography>
            <TextField
              fullWidth multiline rows={3} label="Rejection reason (required)"
              value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              helperText="Explain what needs to change before resubmission. This is sent to the employee."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={rejectTimesheet}>Reject</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
